import React, { useState, useEffect } from 'react';
import { Modal, Slider, Button, message } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import cn from 'classnames';
import { ReactComponent as PlayIcon } from '../../../../resource/PlayIcon.svg';
import { ReactComponent as PauseIcon } from '../../../../resource/PauseIcon.svg';
import {
  currentCallInfoSelector,
  currentRecordUrlSelector,
  listenRecordModalVisibleSelector,
} from '../../store/callHistorySelectors';
import {
  clearCurrentCallInfo,
  clearCurrentRecordUrl,
  setListenModalVisible,
} from '../../store/callHistoryActions';
import { posFormatter } from '../CallList/utils';
import { formatPhoneNumber } from '../../../../helpers/formatPhoneNumber';
import styles from './ListenRecordModal.module.scss';

const ListenRecordModal = () => {
  const dispatch = useDispatch();

  const visible = useSelector(listenRecordModalVisibleSelector);
  const recordUrl = useSelector(currentRecordUrlSelector);
  const callInfo = useSelector(currentCallInfoSelector);

  const [audio] = useState(new Audio());
  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);

  const resetPlayer = () => {
    audio.pause();
    audio.currentTime = 0;
    setIsPlaying(false);
    setCurrentTime(0);
  };

  const play = () => {
    audio
      .play()
      .then(() => {
        setIsPlaying(true);
      })
      .catch((err) => {
        setIsPlaying(false);
        message.error(err.message);
      });
  };

  const togglePlayPause = () => {
    if (isPlaying) {
      audio.pause();
      setIsPlaying(false);
    } else {
      play();
    }
  };

  const handleCancel = () => {
    dispatch(setListenModalVisible(false));
    dispatch(clearCurrentRecordUrl());
    dispatch(clearCurrentCallInfo());
    resetPlayer();
  };

  const handleSliderChange = (value) => {
    setCurrentTime(value);
    audio.currentTime = value;
  };

  // устанавливаем и убираем слушатели событий для audio
  useEffect(() => {
    const handleLoadetMetaData = () => {
      setDuration(audio.duration);
    };
    const handleTimeUpdate = () => {
      setCurrentTime(audio.currentTime);
    };

    const handleEnded = () => {
      resetPlayer();
    };

    audio.addEventListener('loadedmetadata', handleLoadetMetaData);
    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('ended', handleEnded);

    return () => {
      audio.removeEventListener('loadedmetadata', handleLoadetMetaData);
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('ended', handleEnded);
    };
  }, [audio]);

  // Устанавливаем src для audio
  useEffect(() => {
    audio.src = recordUrl;
  }, [recordUrl, audio]);

  // Запускаем запись, когда открывается модалка
  useEffect(() => {
    if (visible) {
      play();
    }
  }, [visible]);

  const formattedPhoneNum = formatPhoneNumber({
    phoneNumber: callInfo?.callerId || '',
    format: `(xxx) xxx-xx-xx`,
    placeholder: 'x',
  });

  const modalTitle =
    callInfo?.direction === 'in'
      ? `Запись входящего звонка с номера ${formattedPhoneNum}`
      : `Запись исходящего звонка на номер ${formattedPhoneNum}`;

  return (
    <Modal
      title={modalTitle}
      visible={visible}
      footer={null}
      onCancel={handleCancel}
      centered
      destroyOnClose
      maskClosable={false}
      wrapClassName={styles.root}
      bodyStyle={{
        display: 'flex',
        alignItems: 'center',
        gap: '14px',
      }}
    >
      <Button
        shape="circle"
        type="primary"
        onClick={togglePlayPause}
        className={styles.playPauseBtn}
      >
        {isPlaying ? (
          <PauseIcon className={styles.icon} />
        ) : (
          <PlayIcon className={cn(styles.icon, styles.playIcon)} />
        )}
      </Button>
      <div className={styles.sliderWrap}>
        <Slider
          max={Math.round(duration)}
          value={currentTime}
          tipFormatter={posFormatter}
          onChange={handleSliderChange}
          className={styles.slider}
        />
        <p className={styles.metaDataContent}>
          <span>{posFormatter(Math.round(currentTime))} </span>/
          <span> {posFormatter(Math.round(duration))}</span>
        </p>
      </div>
    </Modal>
  );
};

export default ListenRecordModal;
